import { alpha } from '@mui/material/styles';

/**
 * ECharts options come from the API pre-styled for a light canvas. This
 * rewrites the chrome (axes, legend, tooltip, map, visual map) to follow the
 * active MUI theme so charts read correctly in both modes.
 */
export const applyEChartsTheme = (option, theme) => {
    if (!option) return option;
    const { palette } = theme;
    const isDark = palette.mode === 'dark';

    const applyAxisTheme = (axis) => !axis ? axis : ({
        ...axis,
        axisLine: { ...axis.axisLine, lineStyle: { ...axis.axisLine?.lineStyle, color: palette.divider } },
        axisTick: { ...axis.axisTick, lineStyle: { ...axis.axisTick?.lineStyle, color: palette.divider } },
        axisLabel: { ...axis.axisLabel, color: palette.text.secondary },
        splitLine: {
            ...axis.splitLine,
            lineStyle: { ...axis.splitLine?.lineStyle, color: alpha(palette.divider, isDark ? 0.75 : 1) },
        },
    });
    const themeAxes = (axis) => Array.isArray(axis) ? axis.map(applyAxisTheme) : applyAxisTheme(axis);

    // Labels drawn *outside* a mark (e.g. the count to the right of an ADI bar)
    // sit on the card, so they need the card's text colour. Labels drawn inside
    // a mark sit on the series colour and are left alone — ECharts already
    // picks a contrasting colour for those.
    const isOutsideLabel = (label) =>
        label?.show && !String(label.position ?? '').startsWith('inside');

    // Geo/map series carry their own land + border colours.
    const themeSeries = (series) => {
        if (!series) return series;
        const one = (s) => {
            if (!s) return s;
            if (s.type !== 'map' && !s.map) {
                return isOutsideLabel(s.label)
                    ? { ...s, label: { ...s.label, color: palette.text.primary, textBorderWidth: 0 } }
                    : s;
            }
            return {
                ...s,
                itemStyle: {
                    ...s.itemStyle,
                    borderColor: palette.surface.border,
                    areaColor: s.itemStyle?.areaColor ?? palette.surface.sunken,
                },
                label: { ...s.label, color: palette.text.secondary },
                emphasis: {
                    ...s.emphasis,
                    label: { ...s.emphasis?.label, color: palette.text.primary },
                    itemStyle: { ...s.emphasis?.itemStyle, borderColor: palette.primary.main },
                },
            };
        };
        return Array.isArray(series) ? series.map(one) : one(series);
    };

    const themeVisualMap = (vm) => {
        if (!vm) return vm;
        const one = (v) => ({ ...v, textStyle: { ...v?.textStyle, color: palette.text.secondary } });
        return Array.isArray(vm) ? vm.map(one) : one(vm);
    };

    return {
        ...option,
        backgroundColor: 'transparent',
        textStyle: { ...option.textStyle, color: palette.text.primary },
        color: isDark
            ? [palette.primary.main, palette.secondary.main, palette.success.main, palette.warning.main, palette.info.main, palette.error.main]
            : option.color,
        title: option.title
            ? { ...option.title, textStyle: { ...option.title.textStyle, color: palette.text.primary } }
            : option.title,
        tooltip: {
            ...option.tooltip,
            backgroundColor: alpha(palette.background.paper, 0.98),
            borderColor: palette.divider,
            textStyle: { ...option.tooltip?.textStyle, color: palette.text.primary },
        },
        legend: { ...option.legend, textStyle: { ...option.legend?.textStyle, color: palette.text.secondary } },
        xAxis: themeAxes(option.xAxis),
        yAxis: themeAxes(option.yAxis),
        visualMap: themeVisualMap(option.visualMap),
        geo: option.geo
            ? {
                ...option.geo,
                itemStyle: {
                    ...option.geo.itemStyle,
                    borderColor: palette.surface.border,
                    areaColor: option.geo.itemStyle?.areaColor ?? palette.surface.sunken,
                },
            }
            : option.geo,
        series: themeSeries(option.series),
    };
};

export default applyEChartsTheme;
